import React, { useEffect, useState, useRef, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, useHistory, Link as RouterLink } from 'react-router-dom';
import { DateTimePicker } from '@material-ui/pickers';
import { SketchPicker } from 'react-color'
import { DateTime } from 'luxon';
import { EditorState } from 'draft-js';
import { htmlFromEditorState, editorStateFromHtml } from "app/utils/DraftConvert";
import { cloneDeep } from 'lodash';
import * as XLSX from 'xlsx';

import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Fab from '@material-ui/core/Fab';
import Collapse from '@material-ui/core/Collapse';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Grid from '@material-ui/core/Grid';
import Popper from '@material-ui/core/Popper';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Alert from '@material-ui/lab/Alert';
import FormControl from '@material-ui/core/FormControl';
import Checkbox from '@material-ui/core/Checkbox';
import Switch from '@material-ui/core/Switch';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Popover from '@material-ui/core/Popover';
import Zoom from '@material-ui/core/Zoom';

import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import SubjectIcon from '@material-ui/icons/Subject';
import ImageIcon from '@material-ui/icons/Image';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import LinkIcon from '@material-ui/icons/Link';
import DeleteIcon from '@material-ui/icons/Delete';
import AttachmentIcon from '@material-ui/icons/Attachment';
import UploadIcon from '@material-ui/icons/CloudUpload';

// Local imports
import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

import { containerFlex, containerFlexFixed, containerFlexGrow, navWidth } from 'app/styles/new-layout';
import { paper } from 'app/styles/core';

import Loading from 'app/components/loaders/Loading';
import RichTextEditor from 'app/components/rte/RichTextEditor';
import RichImageEditor from 'app/components/images/RichImageEditor';
import RichLinkEditor from 'app/components/rte/RichLinkEditor';
import SelectFileDialog from 'app/components/drive/SelectFileDialog';
import EmailTemplatesDialog from 'app/components/email/EmailTemplatesDialog';
import RedButton from 'app/components/buttons/RedButton';

const useBlockStyles = makeStyles(theme => ({
    blockContainer: props => ({
        position: 'relative',
        borderBottom: props.active ? `1px solid #ddd` : null
    }),
    blockControls: {
        position: 'absolute',
        left: '-66px',
        width: '66px',
        top: 0,
        bottom: 0
    },
    fab: {
        backgroundColor: '#ffffff'
    }
}));

const Block = (props) => {
    const { active, handleSelect, index, handleOptions, options, onMoveUp, onMoveDown, onRemove } = props;

    const classes = useBlockStyles({ active: active });

    const controls = (
        <Grid container wrap="nowrap" direction="column" justifyContent="space-between" alignItems='center' className={classes.blockControls}>
            <Grid item>
                <Zoom in={active}>
                    <Fab
                        aria-label="Move Up"
                        size="small"
                        onClick={(e) => onMoveUp(index, e)}
                        className={classes.fab}
                        >
                        <ExpandLessIcon />
                    </Fab>
                </Zoom>
            </Grid>
            <Grid item>
                <Zoom in={active}>
                    <Fab
                        aria-label="Remove Block"
                        size="small"
                        onClick={(e) => onRemove(index, e)}
                        className={classes.fab}
                        >
                        <DeleteIcon />
                    </Fab>
                </Zoom>
            </Grid>
            <Grid item>
                <Zoom in={active}>
                    <Fab
                        aria-label="Move Down"
                        size="small"
                        onClick={(e) => onMoveDown(index, e)}
                        className={classes.fab}
                        >
                        <ExpandMoreIcon />
                    </Fab>
                </Zoom>
            </Grid>
        </Grid>
    )

    switch (options.type) {
        case 'text': return (
            <div className={classes.blockContainer} onClick={(event) => handleSelect(index, event)}>
                <RichTextEditor
                    active={active}
                    handleOptions={(name, value) => handleOptions(index, value, name)}
                    options={options}
                    editorState={options.editor}
                    setEditorState={val => handleOptions(index, val, 'editor')}
                />
                {controls}
            </div>
        );
        case 'image': return (
            <div className={classes.blockContainer} onClick={(event) => handleSelect(index, event)}>
                <RichImageEditor
                    options={options}
                    handleOptions={(name, value) => handleOptions(index, value, name)}
                    active={active}
                    type="email"
                />
                {controls}
            </div>
        )
        case 'link': return (
            <div className={classes.blockContainer} onClick={(event) => handleSelect(index, event)}>
                <RichLinkEditor
                    options={options}
                    handleOptions={(name, value) => handleOptions(index, value, name)}
                    active={active}
                    includeEmailNotice
                />
                {controls}
            </div>
        )
    }
}

const useListItemStyles = makeStyles(theme => ({
    nested: props => ({
        paddingLeft: (10 + props.depth * 14),
    }),
    listItemIcon: {
        minWidth: 0
    }
}));

const findChildIds = (children) => {

    let ids = [];

    children.forEach(c => {

        ids.push(c.id);

        if (c.children_with_count.length > 0) {
            ids = ids.concat(findChildIds(c.children_with_count));
        }
    })

    return ids;
}

const NestedLevelItem = (props) => {

    const { level, depth, levelsChecked, onCheck, onCheckChildren } = props;

    const classes = useListItemStyles({ depth: depth});

    const [ includeChildren, setIncludeChildren ] = useState(false);

    const isSelected = levelsChecked.indexOf(level.id) > -1 ;

    const childIds = (level.children_with_count.length === 0) ? 0 : useMemo(() => findChildIds(level.children_with_count), []);

    const handleIncludeChildren = () => {
        setIncludeChildren(! includeChildren);
        const ids = [...childIds];
        if (! includeChildren) ids.push(level.id);
        onCheckChildren(! includeChildren, ids);
    }

    return (
        <>
            <ListItem
                classes={{ root: classes.nested }}
                button
                dense
                onClick={() => onCheck(level.id)}
                >
                <ListItemIcon classes={{ root: classes.listItemIcon }}>
                    <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                        color="primary"
                    />
                </ListItemIcon>
                <ListItemText
                    primary={ level.title }
                    className={ classes.listItemText }
                />
                <ListItemSecondaryAction>
                    <Typography
                        display="inline"
                        color="textSecondary"
                        variant="caption"
                        >
                        Incl. Sub
                    </Typography>
                    <Switch
                        edge="end"
                        onChange={handleIncludeChildren}
                        checked={includeChildren}
                        disabled={level.children_with_count.length === 0}
                        color="primary"
                    />
                </ListItemSecondaryAction>
            </ListItem>
            {level.children_with_count.map((child) => (
                <NestedLevelItem
                    key={child.id}
                    level={child}
                    depth={(depth + 1)}
                    levelsChecked={levelsChecked}
                    onCheck={onCheck}
                    onCheckChildren={onCheckChildren}
                />
            ))}
        </>
    )
}

const renderDateInput = props => (
    <TextField
        variant="outlined"
        fullWidth
        className={props.className}
        onClick={props.onClick}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        label={props.label}
        helperText={props.helperText}
        {...props.inputProps}
    />
);

const useStyles = makeStyles(theme => ({
    container: {
        ...containerFlex(theme),
        padding: theme.spacing(3),
    },
    content: {
        ...containerFlexGrow(theme),
        position: 'relative',
        paddingRight: theme.spacing(3),

        [theme.breakpoints.down('md')]: {
            paddingRight: '0px',
        }
    },
    nav: {
        ...containerFlexFixed(theme),
        maxWidth: `${navWidth}px`
    },
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    listTitle: {
        paddingTop: theme.spacing(3),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
    buttonGroup: {
        width: '100%',
    },
    buttonExpand: {
        flexGrow: 1
    },
    marginTop: {
        marginTop: theme.spacing(3),
    },
    marginTopSmall: {
        marginTop: theme.spacing(1),
    },
    marginRight: {
        marginRight: theme.spacing(3),
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    marginBottomGrid: {
        marginBottom: theme.spacing(2)
    },
    alert: {
        marginBottom: theme.spacing(3),
        textTransform: 'capitalize'
    },
    fromInput: {
        textAlign: 'right',
    },
    subSection: {
        padding: theme.spacing(1),
    },
    subSectionLoading: {
        textAlign: 'center',
        padding: theme.spacing(3),
    },
    noMarginLeft: {
        marginLeft: 0
    },
    empty: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
    },
    noContentPadding: {
        padding: 0
    },
    email: {
        borderRadius: theme.shape.borderRadius,
        boxShadow: '0px 0px 20px -10px inset',
        minHeight: 200,
        position: 'relative',
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(13),
        paddingLeft: 66,
        paddingRight: 66
    },
    emailContent: {
        maxWidth: 600,
        margin: '0 auto',
    },
    fab: {
        position: 'absolute',
        bottom: theme.spacing(3),
        right: theme.spacing(3),
    },
    colorSwatchContainer: {
    },
    colorSwatch: {
        height: 32,
        width: 32,
        marginRight: theme.spacing(3),
        verticalAlign: 'middle',
        display: 'inline-block',
        borderRadius: theme.shape.borderRadius,
        cursor: 'pointer',
        border: `1px solid ${theme.palette.grey[400]}`
    },
    moveButton: {
        width: '100%'
    },
    scroll: {
        maxHeight: 400,
        overflowY: 'scroll'
    },
    padding: {
        padding: theme.spacing(3)
    },
    emailField: {
        paddingTop: theme.spacing(3) / 2,
        paddingBottom: theme.spacing(3) / 2,
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
    listItemIcon: {
        minWidth: 0
    },
    listItem: {
        paddingLeft: 24
    },
    input: {
        display: 'none',
    },
}));

const initialEmail = {
    id: 0,
    from_name: "",
    from_address: "",
    reply_to_name: "",
    reply_to_address: "",
    subject: "",
    template: false,
    template_title: "",
    status: "new",
    background_color: '#BBC6CC',
    sent_at: DateTime.local().plus({days: 1}).startOf('hour'),
    past_due_group: false
}

const sendOptions = {
    new: ['Send Now', 'Send Later', 'Save as Draft', 'Send Draft', 'Save as Template'],
    draft: ['Update Draft', 'Send Draft', 'Send Now', 'Send Later', 'Save as Template', 'Make a Copy'],
    scheduled: ['Update', 'Save as Draft', 'Send Now', 'Save as Template', 'Make a Copy'],
    sent: ['Save as Template', 'Make a Copy'],
    template: ['Update Template']
}

export default function EditEmail(props) {

    // State and constants
    const classes = useStyles();
    const api = useApi();
    const params = useParams();
    const anchorRef = useRef(null);
    const { app } = useApp();
    const history = useHistory();

    const ids = history.location.state?.ids;

    const bottom = useRef(null);

    const [ email, setEmail ] = useState(initialEmail);
    const [ blocks, setBlocks ] = useState([]);
    const [ to, setTo ] = useState([]);
    const [ groupsChecked, setGroupsChecked ] = useState([]);
    const [ activeBlockKey, setActiveBlockKey] = useState(0);
    const [ attachments, setAttachments ] = useState([]);

    const [ groups, setGroups ] = useState([]);
    const [ levels, setLevels ] = useState([]);

    const [ templates, setTemplates ] = useState([]);

    const [ templateDialogOpen, setTemplateDialogOpen ] = useState(false);

    const [ selectFileDialogOpen, setSelectFileDialogOpen ] = useState(false);
    const [ selectFileType, setSelectFileType ] = useState(null);

    const [ sendButton, setSendButton ] = useState(sendOptions.new[0]);
    const [ sendOptionsOpen, setSendOptionsOpen ] = useState(false);

    const [ loading, setLoading ] = useState(false);
    const [ groupsLoading, setGroupsLoading ] = useState(false);
    const [ levelsLoading, setLevelsLoading ] = useState(false);
    const [ templatesLoading, setTemplatesLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    const [ colorPickerAnchorEl, setColorPickerAnchorEl ] = useState(null);
    const [ activeColor, setActiveColor ] = useState(null);
    const [ handlePickedColor, setHandlePickedColor ] = useState(null);

    const [ newBlockAnchorEl, setNewBlockAnchorEl] = React.useState(null);

    // Getter function
    const getEmail = (id = null) => {

        setLoading(true);

        const getId = id ?? params.id;

        api.get(`/admin/email/${getId}`)
            .then(res => {
                setEmail({...res.email, sent_at: DateTime.fromISO(res.email.sent_at)});
                handleBlocks(res.email.blocks);
                setTo(res.email.to);
                setGroupsChecked(res.email.groups.map(g => g.id));
                setAttachments(res.email.files);
            }).finally(() => setLoading(false));
    };

    const handleBlocks = newBlocks => {
        newBlocks.forEach(b => {
            if (b.options.type === 'text') {
                b.options.editor = editorStateFromHtml(b.options.content);
            }
        })

        setBlocks(newBlocks)
    }

    const getGroups = () => {
        setGroupsLoading(true);

        api.get('/admin/group/group')
            .then(res => setGroups(res.groups))
            .finally(() => setGroupsLoading(false));
    }

    const getLevels = () => {
        setLevelsLoading(true);

        api.get('/admin/group/level/recursive/0')
            .then(res => setLevels(res.groups))
            .finally(() => setLevelsLoading(false));
    }

    const getTemplates = () => {
        setTemplatesLoading(true);

        api.get('/admin/email/template')
            .then(res => setTemplates(res.templates))
            .finally(() => setTemplatesLoading(false));
    }

    useEffect(() => {
        if (params.id > 0) getEmail();
        else {
            const newEmail = {...email};

            newEmail.from_name = app.club.meta.mail_from_name;
            newEmail.from_address = app.club.meta.mail_from_address;
            newEmail.reply_to_name = app.club.meta.mail_reply_to_name;
            newEmail.reply_to_address = app.club.meta.mail_reply_to_address;
            //newEmail.background_color = lighten(app.club.branding.primary_color, 0.85)

            setEmail(newEmail);

            if (history.location.state?.emails) {
                setTo([...history.location.state.emails]);
            }
        }

        getGroups();
        getLevels();
        getTemplates();
    }, []);

    useEffect(() => {
        setSendButton(sendOptions[email.status][0]);
    }, [email.status]);

    // Handlers....................

    const handleChange = name => event => {
        let value;

        if (DateTime.isDateTime(event) || event === null) {
            value = event;
        } else {
            const target = event.target;
            value = (target.type === 'checkbox' ? target.checked : target.value);
        }

        removeError(name);

        setEmail({ ...email, [name]: value });
    }

    const handlePastDueGroup = () => {
        setEmail({ ...email, past_due_group: !email.past_due_group });
    }

    const handleBackgroundColor = (color) => {
        if (color && color.hex) {
            setEmail({ ...email, background_color: color.hex });
            setActiveColor(color.hex);
        }
    }

    const handleGroup = id => {
        const newChecked = [...groupsChecked]
        const i = newChecked.indexOf(id)

        removeError('groups');

        if (i > -1) {
            newChecked.splice(i, 1)
        } else {
            newChecked.push(id)
        }

        setGroupsChecked(newChecked);
    }

    const handleLevelChildren = (addChildren, ids) => {
        let newChecked = [...groupsChecked];

        removeError('groups');

        if (addChildren) {
            newChecked = [...new Set(newChecked.concat(ids))];
        } else {
            newChecked = newChecked.filter((el) => !ids.includes(el));
        }

        setGroupsChecked(newChecked);
    }

    const addTo = () => {
        const newTo = [...to];

        removeError('to');

        newTo.push("");

        setTo(newTo);

        setTimeout(() => {
            bottom.current.scrollIntoView({ behavior: 'smooth' })
        });
    }

    const handleTo = (event, index) => {
        const newTo = [...to]

        newTo[index] = event.target.value;

        setTo(newTo);
    }

    const removeTo = (index) => {
        const newTo = [...to]

        newTo.splice(index, 1);

        setTo(newTo);
    }

    const clearTo = () => {
        setTo([]);
    }

    const handleEmailsUpload = (e) => {
        
        e.preventDefault();
    
        var files = e.target.files, f = files[0];
        var reader = new FileReader();

        reader.onload = function (e) {
            var data = e.target.result;
            let readedData = XLSX.read(data, {type: 'binary'});
            const wsname = readedData.SheetNames[0];
            const ws = readedData.Sheets[wsname];
    
            /* Convert array to json*/
            const dataParse = XLSX.utils.sheet_to_json(ws, {header:1});

            const emailIndex = dataParse[0].findIndex(header => header.toLowerCase() === 'email');

            if (emailIndex > -1) {
                const emails = [];
                
                dataParse.forEach((row, i) => {
                    if (row[emailIndex].length > 0 && i > 0) {
                        emails.push(row[emailIndex]);
                    }
                });

                let newTo = [...to];
                newTo = newTo.concat(emails);
                setTo(newTo);
            }
        };

        reader.readAsBinaryString(f)
        e.target.value='';
    }

    const addBlock = (type, event, file = null) => {

        event?.stopPropagation();

        removeError('blocks');

        const newBlocks = [...blocks];

        const newBlock = {
            id: `new-${blocks.length}`,
            options: {
                type: type,
                background_color: '#FFFFFF',
            }
        };

        switch (type) {
            case 'text':
                newBlock.options.content = null;
                newBlock.options.editor = EditorState.createEmpty();
                newBlock.options.padding_top = 24;
                newBlock.options.padding_right = 24;
                newBlock.options.padding_left = 24;
                newBlock.options.padding_bottom = 24;
                break;
            case 'image':
                newBlock.options.image_align = 'center';
                newBlock.options.image_width = 600;
                newBlock.options.image_width_type = 'percent';
                newBlock.options.content = file;
                newBlock.options.padding_top = 0;
                newBlock.options.padding_right = 0;
                newBlock.options.padding_left = 0;
                newBlock.options.padding_bottom = 0;
                newBlock.options.link = false;
                newBlock.options.link_url = "";
                newBlock.options.target = true;
                break;
            case 'link':
                newBlock.options.link_type = 'link';
                newBlock.options.content = null;
                newBlock.options.padding_top = 24;
                newBlock.options.padding_right = 24;
                newBlock.options.padding_left = 24;
                newBlock.options.padding_bottom = 24;
                newBlock.options.button_text = 'Button Text';
                newBlock.options.button_text_color = '#ffffff';
                newBlock.options.button_color = app.club.branding.primary_color;
                newBlock.options.button_radius = 4;
                newBlock.options.button_align = 'center';
                newBlock.options.button_padding_width = 24;
                newBlock.options.button_padding_height = 8;
                newBlock.options.target = true;
                break;
        }

        newBlocks.push(newBlock);

        setBlocks(newBlocks)
        setActiveBlockKey(blocks.length);
        closeNewBlockDialog();
    }

    const handleBlockOptions = (i, value, name) => {

        const newBlocks = [...blocks]

        newBlocks[i].options[name] = value;

        setBlocks(newBlocks)
    };

    const handleMoveUp = (key, event) => {
        event?.stopPropagation();

        if (key > 0) {
            const previousKey = key - 1;
            const newBlocks = [...blocks];
            const block = newBlocks.splice(key, 1)[0];

            newBlocks.splice(previousKey, 0, block);

            setBlocks(newBlocks);
            setActiveBlockKey(previousKey);
        }
    }

    const handleMoveDown = (key, event) => {
        event?.stopPropagation();

        if (key < (blocks.length - 1)) {
            const nextKey = key + 1;
            const newBlocks = [...blocks];
            const block = newBlocks.splice(key, 1)[0];

            newBlocks.splice(nextKey, 0, block);

            setBlocks(newBlocks);
            setActiveBlockKey(nextKey);
        }
    }

    const handleRemoveBlock = (key, event) => {
        event?.stopPropagation();

        const newBlocks = [...blocks];

        newBlocks.splice(key,1);

        setBlocks(newBlocks);
    }

    const handleActiveBlockKey = (key, e) => {
        e.stopPropagation();

        // Check if image block is still without an image
        if (blocks[activeBlockKey]?.options.type === 'image' && blocks[activeBlockKey]?.options.content == 0 && key !== activeBlockKey) {
            handleRemoveBlock(activeBlockKey);
        }

        setActiveBlockKey(key);
    }

    const handleToggle = () => {
        setSendOptionsOpen(!sendOptionsOpen);
    }

    const handleSendOption = option => {
        setSendButton(option);
        setSendOptionsOpen(false);
    }

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) return;
        setSendOptionsOpen(false);
    };

    const addAttachment = (file) => {
        const newAttachments = [...attachments];

        if (attachments.find(a => a.id === file.id)) {
            setErrors({attachments: 'File has already been added to email.'})
            return 
        }

        if (file.size > 10485760) {
            setErrors({attachments: 'File is too large to be sent as an attachment.'})
            return
        }

        newAttachments.push(file);

        setAttachments(newAttachments);
    }

    const removeAttachment = (key) => {
        const newAttachments = [...attachments];

        newAttachments.splice(key,1);

        setAttachments(newAttachments);
    }

    // Posters....................

    const postEmail = () => {

        setLoading(true);

        if (sendButton === 'Make a Copy') {
            const timer = setTimeout(() => postCopy(), 1000);

            return () => clearTimeout(timer);
        }

        const data = {
            ...email,
            blocks: cloneDeep(blocks),
            groups: [...groupsChecked],
            to: [...to],
            attachments: [...attachments],
            send: sendButton
        };

        if (DateTime.isDateTime(email.sent_at)) {
            data.sent_at = email.sent_at.toSeconds();
        }

        data.blocks.forEach(block => {
            if (block.options.type === 'text') {
                block.options.content = htmlFromEditorState(block.options.editor);
                delete block.options.editor;
            }
        });

        api.post(`/admin/email/${params.id}`, data)
            .then(() => history.push('/admin/emails'))
            .catch((error) => {
                setLoading(false);

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            })
    };

    const archiveEmail = () => {
        setLoading(true);

        api.delete(`/admin/email/${params.id}`).then(() => history.push('/admin/emails'));
    }

    const postCopy = (copy = null, useEmails = true) => {

        if (copy) {
            if (typeof copy === 'number') {
                getEmail(copy);
                return;
            }
            copy.id = 0;
            copy.status = 'new';

            setEmail(copy);
        } else {
            setEmail({ ...email, id: 0, status: 'new' });
        }
        
        setLoading(false);
        setSendButton(sendOptions.new[0]);

        if (copy) {
            if (useEmails) setTo(copy.to);
            setGroupsChecked(copy.groups.map(g => g.id));
            setAttachments(copy.files)
        }

        const newBlocks = copy ? [...copy.blocks] : [...blocks];

        for (let i = 0; i < newBlocks.length; i++) {
            newBlocks[i].id = `new-${i}`;

            if (copy && newBlocks[i].options.type === 'text') {
                newBlocks[i].options.editor = editorStateFromHtml(newBlocks[i].options.content);
            }
        }

        setBlocks(newBlocks);

        history.replace('/admin/emails/0');
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const removeError = field => {
        const newErrors = {...errors};

        delete newErrors[field];

        setErrors(newErrors);
    }

    // Template Dialog....................

    const openTemplateDialog = () => {
        setTemplateDialogOpen(true);
    }

    const closeTemplateDialog = () => {
        setTemplateDialogOpen(false);
    }

    const closeTemplateDialogAndSetCopy = (template, useEmails) => {
        closeTemplateDialog();
        postCopy(template, useEmails);
    }

    // Select File Dialog Functions

    const openSelectFileDialog = (type = null) => {
        setSelectFileDialogOpen(true);
        setSelectFileType(type);
    }

    const closeSelectFileDialog = () => {
        setSelectFileDialogOpen(false);
    }

    const closeSelectFileDialogAndAddBlock = (file, event) => {
        closeSelectFileDialog();

        switch (selectFileType) {
            case 'image': 
                addBlock('image', event, file);
                break;
            default: 
                addAttachment(file);
                closeNewBlockDialog();
        }

        setSelectFileType(null);
    }

    // Color Picker Popover....................

    const openColorPicker = (event, color, handler) => {
        setColorPickerAnchorEl(event.currentTarget);
        setActiveColor(color);
        setHandlePickedColor(() => handler);
    }

    const closeColorPicker= () => {
        setColorPickerAnchorEl(null);
    }

    // New Block Popover....................

    const openNewBlockDialog = (event) => {
        setNewBlockAnchorEl(event.currentTarget);
    }

    const closeNewBlockDialog = () => {
        setNewBlockAnchorEl(null);
    }

    // Constants....................

    const sendText = useMemo(() => {

        switch (email.status) {
            case 'sent': return 'Sent On: ' + DateTime.fromISO(email.sent_at).toLocaleString(DateTime.DATETIME_MED);
            case 'scheduled': return 'Scheduled For: ' + DateTime.fromISO(email.sent_at).toLocaleString(DateTime.DATETIME_MED);
            case 'draft': return 'Draft Email';
            case 'template': return 'This is a template email and can only be updated.';
        }

    }, [email.status])

    const severity = useMemo(() => {
        switch (email.status) {
            case 'sent': return 'success';
            case 'scheduled': return 'warning';
            case 'draft':
            case 'template':
            default: return 'info';
        }
    }, [email.status]);

    return (
        <div className={classes.container} onClick={(e) => handleActiveBlockKey(null, e)}>
            <div className={classes.content}>
                {(email.id > 0) &&
                    <Alert 
                        className={classes.alert} 
                        severity={severity} 
                        action={
                            <Button
                                aria-label="Archive Email"
                                onClick={archiveEmail}
                                >
                                Archive Email
                            </Button>
                        }>
                        {sendText}
                    </Alert>
                }
                <Paper className={classes.paper}>
                    <Loading isLoading={loading}/>
                    <Grid container alignItems="center" spacing={3} className={classes.marginBottomGrid}>
                        <Grid item>
                            <Typography variant="h5" component="h2">{email.status === 'template' ? 'Edit Template' : email.id > 0 ? 'Edit Email' : "New Email"}</Typography>
                        </Grid>
                        <Grid item xs={true}/>
                        <Grid item xs={false}>
                            <Button
                                aria-label="Use a Template"
                                variant="outlined"
                                color="primary"
                                onClick={openTemplateDialog}
                                disabled={email.status === 'template'}
                                >
                                Use a Template
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3} className={classes.marginBottom}>
                        <Grid item xs={6}>
                            <TextField
                                variant='outlined'
                                label='From'
                                value={email.from_name}
                                id="input-from-name"
                                onChange={handleChange('from_name')}
                                fullWidth
                                error={isError('from_name')}
                                helperText={getErrorMessage('from_name')}
                                required
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl variant="outlined" error={isError('from_address')} fullWidth size="small" required>
                                <InputLabel htmlFor="input-from-address">From Address</InputLabel>
                                <OutlinedInput
                                    id="input-from-address"
                                    value={email.from_address}
                                    onChange={handleChange('from_address')}
                                    endAdornment={<InputAdornment position="end">@{app.club.meta.mail_domain}</InputAdornment>}
                                    aria-describedby="from-address-helper-text"
                                    classes={{ input: classes.fromInput }}
                                    placeholder="members"
                                    labelWidth={120}
                                />
                                <FormHelperText id="from-address-helper-text">{getErrorMessage('from_address')}</FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                variant='outlined'
                                label='Reply To'
                                value={email.reply_to_name}
                                id="input-reply-to-name"
                                onChange={handleChange('reply_to_name')}
                                fullWidth
                                error={isError('reply_to_name')}
                                helperText={getErrorMessage('reply_to_name')}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                variant='outlined'
                                label='Reply To Address'
                                value={email.reply_to_address}
                                id="input-reply-to-address"
                                onChange={handleChange('reply_to_address')}
                                fullWidth
                                error={isError('reply_to_address')}
                                helperText={getErrorMessage('reply_to_address')}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                variant='outlined'
                                label='Subject'
                                value={email.subject}
                                id="input-subject"
                                onChange={handleChange('subject')}
                                fullWidth
                                error={isError('subject')}
                                helperText={getErrorMessage('subject')}
                                required
                            />
                        </Grid>
                    </Grid>
                    <div className={classes.email} style={{ backgroundColor: email.background_color }}>
                        <div className={classes.emailContent} >
                            {isError('blocks') &&
                                <Alert severity="error">{getErrorMessage('blocks')}</Alert>
                            }
                            {blocks.map((block, i) => (
                                <Block
                                    key={block.id}
                                    index={i}
                                    active={(activeBlockKey === i)}
                                    options={block.options}
                                    handleSelect={handleActiveBlockKey}
                                    handleOptions={handleBlockOptions}
                                    onMoveUp={handleMoveUp}
                                    onMoveDown={handleMoveDown}
                                    onRemove={handleRemoveBlock}
                                />
                            ))}
                            <Fab
                                aria-label="New Block"
                                color="primary"
                                className={classes.fab}
                                onClick={openNewBlockDialog}
                                >
                                <AddIcon />
                            </Fab>
                        </div>
                    </div>
                    {(attachments.length > 0) &&
                        <List className={classes.marginTop} dense>
                            {attachments.map((a, i) => (
                                <ListItem ContainerComponent="div" key={i}>
                                    <ListItemIcon>
                                        <AttachmentIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={a.name} primaryTypographyProps={{color: 'primary'}} secondary={a.size_formatted}/>
                                    <ListItemSecondaryAction>
                                        <IconButton aria-label="Remove File" onClick={() => removeAttachment(i)}>
                                            <CloseIcon fontSize="small"/>
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    }
                    {isError('attachments') &&
                        <Alert className={classes.marginTop} severity="error" onClose={() => removeError('attachments')}>{getErrorMessage('attachments')}</Alert>
                    }
                    <Popover
                        open={Boolean(newBlockAnchorEl)}
                        anchorEl={newBlockAnchorEl}
                        onClose={closeNewBlockDialog}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        >
                        <List
                            subheader={
                                <ListSubheader component="div" id="nested-list-subheader">
                                    Select a block to add to your email
                                </ListSubheader>
                            }
                            className={classes.root}
                            >
                            <ListItem button onClick={(event) => addBlock('text', event)}>
                                <ListItemIcon>
                                    <SubjectIcon />
                                </ListItemIcon>
                                <ListItemText primary="Text" />
                            </ListItem>
                            <ListItem button onClick={() => openSelectFileDialog("image")}>
                                <ListItemIcon>
                                    <ImageIcon />
                                </ListItemIcon>
                                <ListItemText primary="Image" />
                            </ListItem>
                            <ListItem button onClick={(event) => addBlock('link', event)}>
                                <ListItemIcon>
                                    <LinkIcon />
                                </ListItemIcon>
                                <ListItemText primary="Link" />
                            </ListItem>
                            <ListItem button onClick={() => openSelectFileDialog(null)}>
                                <ListItemIcon>
                                    <AttachmentIcon />
                                </ListItemIcon>
                                <ListItemText primary="Attachment" />
                            </ListItem>
                        </List>
                    </Popover>
                </Paper>
            </div>
            <div className={classes.nav}>
                <Paper className={classes.paper}>
                    <Typography variant="h6" className={classes.marginBottom}>
                        Email Styling
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Email Background Color
                    </Typography>
                    <div className={classes.colorSwatchContainer}>
                        <span
                            className={classes.colorSwatch}
                            style={{ backgroundColor: email.background_color }}
                            onClick={(event) => openColorPicker(event, email.background_color, handleBackgroundColor)}
                        />
                        <Typography variant="body2" color="textSecondary" component="span">{email.background_color}</Typography>
                    </div>
                </Paper>
                <Paper className={classes.marginBottom}>
                    <Loading isLoading={(groupsLoading || levelsLoading)}/>
                    <Typography variant="h6" className={classes.listTitle}>
                        Select Email Group
                    </Typography>
                    <div className={classes.scroll}>
                        <List className={classes.noContentPadding}>
                            {groups.map(group => (
                                <ListItem
                                    classes={{ root: classes.listItem }}
                                    button
                                    dense
                                    onClick={() => handleGroup(group.id)}
                                    key={'group' + group.id}
                                    >
                                    <ListItemIcon classes={{ root: classes.listItemIcon }}>
                                        <Checkbox
                                            edge="start"
                                            checked={(groupsChecked.indexOf(group.id) > -1)}
                                            tabIndex={-1}
                                            disableRipple
                                            color="primary"
                                        />
                                    </ListItemIcon>
                                    <ListItemText primary={group.title}/>
                                </ListItem>
                            ))}
                            {levels.map(level => (
                                <NestedLevelItem
                                    key={level.id}
                                    level={level}
                                    depth={1}
                                    levelsChecked={groupsChecked}
                                    onCheck={handleGroup}
                                    onCheckChildren={handleLevelChildren}
                                />
                            ))}
                            <ListItem
                                classes={{ root: classes.listItem }}
                                button
                                dense
                                onClick={handlePastDueGroup}
                                >
                                <ListItemIcon classes={{ root: classes.listItemIcon }}>
                                    <Checkbox
                                        edge="start"
                                        checked={email.past_due_group}
                                        tabIndex={-1}
                                        disableRipple
                                        color="primary"
                                    />
                                </ListItemIcon>
                                <ListItemText primary="Past Due Members"/>
                            </ListItem>
                        </List>
                    </div>
                    {isError('groups') &&
                        <Alert className={classes.marginTop}  severity="error">{getErrorMessage('groups')}</Alert>
                    }
                </Paper>
                <Paper className={classes.marginBottom}>
                    <Typography variant="h6" className={classes.listTitle}>
                        Add Individual Emails
                    </Typography>
                    <div className={classes.scroll}>
                        {to.map((emailAddress, index) => (
                            <FormControl className={classes.emailField} variant="outlined" size="small" fullWidth key={index}>
                                <OutlinedInput
                                    value={emailAddress}
                                    onChange={(value) => handleTo(value, index)}
                                    labelWidth={0}
                                    placeholder="user@example.com"
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="remove email address"
                                                onClick={() => removeTo(index)}
                                                edge="end"
                                                >
                                                <CloseIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                        </FormControl>
                        ))}
                        <div ref={bottom}/>
                    </div>
                    <div className={classes.padding}>
                        <Button
                            aria-label="Add New Email Address"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            color="primary"
                            onClick={addTo}
                            size="small"
                            fullWidth
                            >
                            New Email Address
                        </Button>
                        <input
                            className={classes.input}
                            id="import-email-spreadsheet"
                            type="file"
                            onChange={handleEmailsUpload}
                        />
                        <label htmlFor="import-email-spreadsheet">
                            <Button
                                aria-label="Upload Email Addresses"
                                variant="outlined"
                                startIcon={<UploadIcon />}
                                color="primary"
                                size="small"
                                fullWidth
                                className={classes.marginTop}
                                component="span"
                                >
                                Upload Email Addresses
                            </Button>
                        </label>
                        <RedButton
                            aria-label="Clear All"
                            variant="outlined"
                            onClick={clearTo}
                            size="small"
                            fullWidth
                            className={classes.marginTop}
                            >
                            Clear All Addresses
                        </RedButton>
                    </div>
                    {isError('to') &&
                        <Alert className={classes.marginTop} severity="error">{getErrorMessage('to')}</Alert>
                    }
                </Paper>
                <Paper className={classes.paper}>
                    <ButtonGroup
                        variant="contained"
                        color="primary"
                        ref={anchorRef}
                        aria-label="Send Email"
                        className={classes.buttonGroup}
                        disabled={loading}
                        >
                        <Button className={classes.buttonExpand} onClick={postEmail}>
                            {sendButton}
                        </Button>
                        <Button
                            color="primary"
                            size="small"
                            aria-controls={sendOptionsOpen ? 'send-options-menu' : undefined}
                            aria-expanded={sendOptionsOpen ? 'true' : undefined}
                            aria-label="Select Send Option"
                            aria-haspopup="menu"
                            onClick={handleToggle}
                            >
                            <ArrowDropDownIcon />
                        </Button>
                    </ButtonGroup>
                    <Popper
                        open={sendOptionsOpen}
                        anchorEl={anchorRef.current}
                        role={undefined}
                        transition
                        placement="bottom-end"
                        >
                        {({ TransitionProps }) => (
                            <Grow
                                {...TransitionProps}
                                style={{
                                    transformOrigin: 'center top',
                                }}
                                >
                                <Paper>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <MenuList id="send-options-menu">
                                            {sendOptions[email.status].map((option, index) => (
                                                <MenuItem
                                                    key={index}
                                                    selected={option === sendButton}
                                                    onClick={() => handleSendOption(option)}
                                                    >
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </MenuList>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                    <Collapse in={sendButton === 'Send Later' || email.status === 'scheduled'}>
                        <DateTimePicker
                            label="Send Date"
                            inputVariant="outlined"
                            value={email.sent_at}
                            onChange={handleChange('sent_at')}
                            variant="outlined"
                            TextFieldComponent={renderDateInput}
                            minutesStep={5}
                            className={classes.marginTop}
                            disablePast
                            clearable
                        />
                    </Collapse>
                    <Collapse in={sendButton === 'Save as Template' || email.status === 'template'}>
                        <TextField
                            variant='outlined'
                            label='Template Name'
                            value={email.template_title}
                            id="input-template-title"
                            onChange={handleChange('template_title')}
                            fullWidth
                            error={isError('template_title')}
                            helperText={getErrorMessage('template_title')}
                            className={classes.marginTop}
                            required
                        />
                    </Collapse>
                    <Collapse in={sendButton === 'Send Draft'}>
                        <Typography
                            display="block"
                            color="error"
                            variant="caption"
                            className={classes.marginTopSmall}
                            >
                            <strong>Draft Emails will ONLY be sent to Individual Emails</strong>
                        </Typography>
                    </Collapse>
                    <Button
                        className={classes.marginTop}
                        variant="outlined"
                        fullWidth
                        component={RouterLink}
                        to="/admin/emails"
                        >
                        Back to Emails
                    </Button>
                </Paper>
            </div>
            <Popover
                open={Boolean(colorPickerAnchorEl)}
                anchorEl={colorPickerAnchorEl}
                onClose={closeColorPicker}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                >
                <SketchPicker color={ activeColor } onChangeComplete={ handlePickedColor } />
            </Popover>
            <SelectFileDialog
                open={selectFileDialogOpen}
                onSelect={closeSelectFileDialogAndAddBlock}
                onClose={closeSelectFileDialog}
                type={selectFileType}
            />
            <EmailTemplatesDialog
                open={templateDialogOpen}
                onClose={closeTemplateDialog}
                onSelect={closeTemplateDialogAndSetCopy}
                templates={templates}
                loading={templatesLoading}
            />
        </div>
    )
}
